import { useEffect, useState } from "react"; // Removed useRef, no longer needed here
import PropTypes from "prop-types";
import { useNavigate, useMatch } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth0 } from "@auth0/auth0-react";

// @mui material components
import { Grid, Card, Box, Tab, Tabs } from "@mui/material";
import { tabsClasses } from "@mui/material/Tabs";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";

// Local & Custom Components
import Map from "components/Maps/Map";
import CameraLatest from "components/Camera/CameraLatest";
import RevisedTable from "components/Tables/RevisedTable";
import BKSGroupSettings from "components/Forms/BKSGroupSettings"; // Added this back from your snippet

// APIs & Utilities
import groupAPI from "services/api/groups";
import bksAPI from "services/api/bks";
import sitesAPI from "services/api/sites";

import { disableMap } from "utils/map";
import { useSoftUIController } from "hooks/context";
import { wrapper, loadingScreen } from "utils/wrapper";
import { setupGroupTabSelectors } from "utils/setup";
import { createDataTableRows } from "utils/device";
import { getNamingConventions } from "utils/formatting";
import { processStopmonitorGroupData } from "utils/group";

const MAPBOX_SETTINGS = {
    style: "mapbox://styles/harrymontano/cm01omrnz000u01omf2uegk93?optimize=true",
    zoom: 12,
    interactive: true,
};

function CustomTabPanel({ showTabs, children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            style={{
                height: "100%",
                width: "100%",
                overflowX: "hidden",
                display: value === index ? 'block' : 'none'
            }}
            {...other}
        >
            <Box sx={{ px: 1, pt: showTabs ? 0 : 1, height: `calc(100% - ${showTabs ? 0 : '16px'})`, width: "calc(100% - 16px)" }}>
                {children}
            </Box>
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    value: PropTypes.number.isRequired,
    index: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        "aria-controls": `simple-tabpanel-${index}`,
    };
}

function DeviceGroupView({ map, user }) {
    const [controller] = useSoftUIController();
    const { deviceLayout, selectedOrg, userPermissions, appearance } = controller;
    const isMobile = deviceLayout === "mobile";

    const auth0 = useAuth0();
    const navigate = useNavigate();
    const match = useMatch("/groups/:group_id");
    const group_id = match?.params?.group_id;

    const [tabValue, setTabValue] = useState(0);

    // Load API tokens safely
    useEffect(() => {
        groupAPI.loadToken(auth0);
        bksAPI.loadToken(auth0);
        sitesAPI.loadToken(auth0);
    }, [auth0]);

    // Handle map disabling
    useEffect(() => {
        if (map?.current) {
            disableMap(map, user, selectedOrg);
        }
    }, [map, user, selectedOrg]);

    const showAdvancedDeviceLevel = userPermissions.includes("devices-advanced:view");

    // React Query Calls
    const devices = useQuery({
        queryKey: ["getAllDevices", selectedOrg],
        queryFn: () => bksAPI.getAllDevices(),
    });

    const sites = useQuery({
        queryKey: ['getAllSites', selectedOrg],
        queryFn: async () => await sitesAPI.getAllSites()
    });

    const group = useQuery({
        queryKey: ["getGroupWithID", group_id, selectedOrg],
        queryFn: () => groupAPI.getGroupWithID(group_id),
        enabled: !!group_id,
    });

    if (group.isLoading || sites.isLoading || devices.isLoading) {
        return loadingScreen(controller, true);
    }

    if (group.error || sites.error || devices.error) {
        console.error(group.error || sites.error || devices.error);
        return wrapper("Error!", controller);
    }

    if (!group.data || !sites.data || !devices.data) {
        return loadingScreen(controller, false);
    }

    const groupData = processStopmonitorGroupData(group.dataUpdatedAt, group.data, sites.data, devices.data, showAdvancedDeviceLevel);
    const dataTableData = createDataTableRows(groupData.entities, showAdvancedDeviceLevel);

    let polygonColour = "#58ba2d";
    if (!groupData.all_updated) {
        polygonColour = "#ff667c";
    }
    else if (groupData.devices_monitored > 0 && showAdvancedDeviceLevel) {
        polygonColour = "#edaa00";
    }

    const baseTabs = setupGroupTabSelectors(userPermissions, groupData);
    let tabSelectors = [...baseTabs];

    // Inject Map and Next Event into tabs if on mobile layout
    if (isMobile) {
        const overviewIdx = tabSelectors.indexOf("Overview");
        if (overviewIdx !== -1) {
            tabSelectors.splice(overviewIdx + 1, 0, "Map");
        } else {
            tabSelectors.push("Map");
        }
    }

    const showTabs = tabSelectors.length > 1;

    const activeCount = groupData.entities.filter((d) => d.active).length;
    const reservedCount = groupData.entities.filter((d) => !d.active).length;

    const headerValues = (
        <SoftBox display="flex" pb={1} pt={0.5} flexDirection="row" justifyContent="space-between" alignItems="center">
            <SoftBox>
                <SoftTypography variant={deviceLayout == "mobile" ? "h5" : "h4"} fontWeight="bold" sx={{ mr: 0.5, flexShrink: 1, width: 'fit-content' }}>{group.data.name}</SoftTypography>
                <SoftTypography variant={deviceLayout == "mobile" ? "subtitle3" : "subtitle2"} fontWeight="medium" color="text" textTransform="capitalize" sx={{ minWidth: "fit-content" }}>{group.data.type} Group</SoftTypography>
            </SoftBox>
            {groupData.sites.length > 0 ? (
                <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="flex-end" gap={0.5}>
                    <SoftTypography lineHeight={1.4} variant="h6" fontWeight="semi-bold" color="dark">
                        {`${activeCount} Total Sites${activeCount !== 1 ? "s" : ""}`}
                    </SoftTypography>
                </SoftBox>
            ) : groupData.uuids.length > 0 ? (
                <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="flex-end" gap={0.5}>
                    <SoftTypography lineHeight={1.4} variant="h6" fontWeight="semi-bold" color="dark">
                        {`${activeCount} Active Device${activeCount !== 1 ? "s" : ""}`}
                    </SoftTypography>
                    {showAdvancedDeviceLevel && (
                        <SoftTypography lineHeight={1.4} variant="caption" fontWeight="regular" color="text">
                            {`${reservedCount} Hidden Device${reservedCount !== 1 ? "s" : ""}`}
                        </SoftTypography>
                    )}
                </SoftBox>
            ) : (
                <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="flex-end" gap={0.5}>
                    <SoftTypography lineHeight={1.4} variant="h6" fontWeight="semi-bold" color="dark">
                        {`${activeCount} Total Locations${activeCount !== 1 ? "s" : ""}`}
                    </SoftTypography>
                </SoftBox>
            )}
        </SoftBox>
    );

    const cameraDevices = groupData.devices.filter((d) => d.hardware_type === "bk-camera");

    // Reusable components for layout rendering
    // FIX: Removed the importedMap prop. The Map component will now use its own isolated refs.
    const renderMapComponent = () => (
        <Card onClick={(e) => e.stopPropagation()} sx={{ minHeight: "200px", height: "100%", overflow: "hidden" }}>
            <Map
                selected_group={groupData}
                parent_group={groupData.parent_group}
                allow_devices={true}
                colour={polygonColour}
                settings={MAPBOX_SETTINGS}
                padding={30}
            />
        </Card>
    );

    // Dynamic Index Helpers
    const getTabIndex = (name) => tabSelectors.indexOf(name);

    return wrapper((
        <SoftBox mb={1} sx={({ palette, functions: { rgba } }) => ({ borderRadius: "20px", backgroundColor: rgba(palette[appearance].background, 0.7), overflow: "hidden", height: 'calc(100%)', maxWidth: "100%" })}>
            {showTabs && (
                <Grid container spacing={0} sx={{ width: "100%", margin: 0 }}>
                    <Grid item xxs={12}>
                        <SoftBox p={1}>
                            <Tabs
                                value={tabValue}
                                onChange={(e, val) => setTabValue(val)}
                                aria-label="GroupView Tabs"
                                allowScrollButtonsMobile
                                variant="scrollable"
                                scrollButtons
                                sx={{
                                    maxWidth: "100%",
                                    [`& .${tabsClasses.scrollButtons}`]: {
                                        "&.Mui-disabled": { opacity: 0 },
                                    },
                                    "& .MuiTabs-indicator": {
                                        top: "3px",
                                        height: "calc(100% - 6px)",
                                    },
                                }}
                            >
                                {tabSelectors.map((row, index) => (
                                    <Tab
                                        key={row}
                                        sx={{ minWidth: "fit-content !important", padding: "12px !important" }}
                                        label={row}
                                        {...a11yProps(index)}
                                    />
                                ))}
                            </Tabs>
                        </SoftBox>
                    </Grid>
                </Grid>
            )}

            {/* OVERVIEW TAB */}
            <CustomTabPanel showTabs={showTabs} value={tabValue} index={getTabIndex("Overview")}>
                <SoftBox
                    borderRadius="16px"
                    color="white"
                    width="100%"
                    display="flex"
                    height={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : isMobile ? "4.5rem" : "6.5rem"} - 16px)`}
                    minHeight={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : isMobile ? "4.5rem" : "6.5rem"} - 16px)`}
                    sx={{ overflow: "hidden" }}
                >
                    <SoftBox display="flex" height="100%" width="100%" flex={1} gap={1} sx={{ overflow: "hidden" }}>
                        <SoftBox display="flex" flex={{ lg: 7, x3l: 8 }} flexDirection="column" height="100%" minHeight={0} minWidth={0}>
                            <RevisedTable
                                header={headerValues}
                                table={dataTableData}
                                showAdvancedDeviceLevel={showAdvancedDeviceLevel}
                                canSearch
                            />
                        </SoftBox>

                        {/* Desktop Side Column with Map and Event (Hidden on Mobile) */}
                        {!isMobile && (
                            <SoftBox display="flex" flex={{ lg: 5, x3l: 4 }} flexDirection="column" height="100%" minWidth={0}>
                                <SoftBox sx={{ minHeight: "200px", height: "100%" }}>
                                    {renderMapComponent()}
                                </SoftBox>
                            </SoftBox>
                        )}
                    </SoftBox>
                </SoftBox>
            </CustomTabPanel>

            {/* MOBILE MAP TAB */}
            {isMobile && (
                <CustomTabPanel showTabs={showTabs} value={tabValue} index={getTabIndex("Map")}>
                    <SoftBox height={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : "4.5rem"} - 16px)`} width="100%" sx={{ borderRadius: '16px' }}>
                        {renderMapComponent()}
                    </SoftBox>
                </CustomTabPanel>
            )}

            {/* CAMERA FEEDS TAB */}
            <CustomTabPanel showTabs={showTabs} value={tabValue} index={getTabIndex("Camera Feeds")}>
                <SoftBox height={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : "4.5rem"} - 16px)`} width="100%" sx={{ overflowY: 'auto', background: '#eee', borderRadius: '16px' }}>
                    <Grid container>
                        {cameraDevices.map((row, index) => (
                            <Grid item xxs={12} sm={6} md={4} lg={3} xl={2} key={row.uuid || index}>
                                <SoftBox m={1}>
                                    <Card>
                                        <SoftBox p={1}>
                                            <SoftTypography pl={1} pt={1} variant="h6" fontWeight="regular" color="text" textAlign="center">
                                                eMinor {row.name}
                                            </SoftTypography>
                                            <CameraLatest uuid={row.uuid} no_padding={true} />
                                        </SoftBox>
                                    </Card>
                                </SoftBox>
                            </Grid>
                        ))}
                    </Grid>
                </SoftBox>
            </CustomTabPanel>

            {/* GROUP SETTINGS TAB */}
            <CustomTabPanel showTabs={showTabs} value={tabValue} index={getTabIndex("Group Settings")}>
                <SoftBox
                    borderRadius="16px"
                    color="white"
                    width="100%"
                    display="flex"
                    height={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : isMobile ? "4.5rem" : "6.5rem"} - 16px)`}
                    minHeight={`calc(100vh - ${showTabs ? (isMobile ? "8.5rem" : "10.25rem") : isMobile ? "4.5rem" : "6.5rem"} - 16px)`}
                    sx={{ overflow: "hidden" }}
                >
                    <SoftBox display="flex" height="100%" width="100%" flex={1} gap={1} sx={{ overflow: "hidden" }}>
                        <SoftBox flex={{ xxs: 12, lg: 7, x3l: 8 }}>
                            <Card sx={{ height: "100%", minHeight: deviceLayout == 'mobile' ? 'calc(100svh - 8.5rem - 16px)' : '100%', overflowY: 'auto' }}>
                                <BKSGroupSettings selected_group={groupData} />
                            </Card>
                        </SoftBox>

                        {!isMobile && (
                            <SoftBox display="flex" flex={{ lg: 5, x3l: 4 }} flexDirection="column" height="100%" minWidth={0}>
                                <SoftBox sx={{ minHeight: "200px", height: "100%" }}>
                                    {renderMapComponent()}
                                </SoftBox>
                            </SoftBox>
                        )}
                    </SoftBox>
                </SoftBox>
            </CustomTabPanel>
        </SoftBox>), controller);
}

DeviceGroupView.propTypes = {
    map: PropTypes.object,
    user: PropTypes.object,
};

export default DeviceGroupView;